import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, ApiError } from './api'

export type Role = 'rep' | 'supervisor' | 'admin' | 'head'

export interface User {
  id: string
  tenantId: string
  email: string
  displayName: string
  role: Role
  salesTeam: string | null
}

export interface ModelInfo {
  modelVersionId: string
  publishedAt: string | null
  basis: 'ex_works' | 'delivered'
  currency: string
  blocks: { key: string; name: string; nameDe?: string | null }[]
  drivers: { id: string; name: string; nameDe: string | null; block: string; kind: string; grain: string; levels: string[]; levelLabels: string[] | null; levelLabelsDe: string[] | null; anchorLow: string | null; anchorHigh: string | null; reversed: boolean }[]
  policy: { concessionRate: number; capRate: number | null; floorIncludesBlocks: string[]; authority: { repFloor: string; hardFloor: string; limits?: unknown }; costMoveBasis: string }
}

export interface Operator {
  id: string
  email: string
  displayName: string
}

export interface TenantInfo {
  name: string
  slug: string
  currency: string
  locale: string
}

interface AuthState {
  loading: boolean
  user: User | null
  operator: Operator | null
  tenant: TenantInfo | null
  model: ModelInfo | null
  providers: { local: boolean; oidc: boolean }
  refresh: () => Promise<void>
  login: (email: string, password: string, tenant?: string) => Promise<void>
  loginOperator: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const Ctx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [operator, setOperator] = useState<Operator | null>(null)
  const [tenant, setTenant] = useState<TenantInfo | null>(null)
  const [model, setModel] = useState<ModelInfo | null>(null)
  const [providers, setProviders] = useState({ local: true, oidc: false })

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<{ user: User | null; operator: Operator | null; tenant: TenantInfo | null }>('/api/auth/me')
      setUser(me.user)
      setOperator(me.operator)
      setTenant(me.tenant)
      if (me.user) {
        try {
          setModel(await api.get<ModelInfo>('/api/model'))
        } catch {
          setModel(null)
        }
      } else setModel(null)
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setUser(null)
        setOperator(null)
        setTenant(null)
        setModel(null)
      } else throw e
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    api.get<{ local: boolean; oidc: boolean }>('/api/auth/providers').then(setProviders).catch(() => {})
    refresh().catch(() => setLoading(false))
  }, [refresh])

  const login = useCallback(
    async (email: string, password: string, tenant?: string) => {
      await api.post('/api/auth/login', { email, password, ...(tenant ? { tenant } : {}) })
      await refresh()
    },
    [refresh],
  )
  const loginOperator = useCallback(
    async (email: string, password: string) => {
      await api.post('/api/auth/platform/login', { email, password })
      await refresh()
    },
    [refresh],
  )
  const logout = useCallback(async () => {
    await api.post('/api/auth/logout')
    setUser(null)
    setOperator(null)
    setTenant(null)
    setModel(null)
  }, [])

  const value = useMemo(
    () => ({ loading, user, operator, tenant, model, providers, refresh, login, loginOperator, logout }),
    [loading, user, operator, tenant, model, providers, refresh, login, loginOperator, logout],
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const v = useContext(Ctx)
  if (!v) throw new Error('useAuth outside AuthProvider')
  return v
}
